/**
 * @file        ToolAccessGate.tsx
 * @description Single presentational gate for a gated tool's access state
 *              (generalised from the former auction-sheet ErrorView, now removed)
 *              that renders all seven ToolAccessState.status branches:
 *              loading (nothing), ok (always-visible remaining counter, INV-C),
 *              cooldown (live MM:SS timer ONLY, INV-B — no auth window, no button;
 *              the tool's own action button is disabled externally by state.status),
 *              anon_exhausted (TelegramAuthBlock, no timer), auth_exhausted
 *              (daily-limit manager card), queue_full (retry + manager) and
 *              error (nothing).
 * @rule        Purely presentational. `state` arrives as a prop — the tool itself owns
 *              useToolAccess and passes state down, so the same state can gate both this
 *              UI and the tool's own "Рассчитать" button (INV-A: one source of truth).
 * @rule        cooldown vs *_exhausted are DISTINCT branches (INV-B): cooldown shows a
 *              countdown ONLY (no button in the gate — the tool's action button is
 *              disabled externally) and NEVER an auth window; anon_exhausted shows
 *              the auth block and NEVER a timer.
 * @rule        `error` renders NOTHING. It means the GET /quota read itself failed;
 *              useToolAccess is fail-open by contract (last-known counter values are
 *              kept), so there is no user-facing event and nothing for the user to act
 *              on — the gate stays silent and the failure goes to console.warn in the
 *              hook. `queue_full` stays the ONLY red panel the gate produces, because
 *              it is a real server condition addressed to the user.
 * @rule        Design tokens only (rounded-2xl, border, bg-secondary, text-text-muted) —
 *              no hex (the Telegram blue lives inside TelegramAuthBlock and is not touched).
 * @universal   knowledge/universals/components.md#toolaccessgate
 */

"use client";

import { useEffect, useState } from "react";
import { CONTACTS } from "@/lib/constants";
import TelegramAuthBlock from "@/components/TelegramAuthBlock";
import type { ToolAccessState } from "@/lib/useToolAccess";

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface ToolAccessGateProps {
  /** Canonical access state from useToolAccess. The tool reads it too (INV-A). */
  state: ToolAccessState;
  /** Originating tool, forwarded to TelegramAuthBlock for source tracking. */
  source: string;
  /** Retry handler for cooldown / queue_full. Optional — omit to hide retry. */
  onRetry?: () => void;
}

// ─── COOLDOWN TIMER (inner sub-component) ────────────────────────────────────

/**
 * Live countdown used only in the cooldown branch. Counts down from the initial
 * value, formats as "MM:SS", stops at 0 and calls `onFinish` once so the parent can
 * re-read access state (onRetry → refetch; status flips cooldown → ok). Owns its
 * own ticking — a display concern that does
 * not leak upward. Kept internal (NOT exported) — the single CooldownTimer in the
 * codebase (the auction ErrorView that had its own was removed in Step 07c).
 */
function CooldownTimer({
  initialSeconds,
  onFinish,
}: {
  initialSeconds: number;
  onFinish: () => void;
}) {
  const [seconds, setSeconds] = useState(initialSeconds);

  useEffect(() => {
    if (seconds <= 0) {
      onFinish();
      return;
    }
    const id = window.setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [seconds, onFinish]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <span className="font-mono text-lg font-semibold text-text" aria-live="off">
      {mm}:{ss}
    </span>
  );
}

// ─── MANAGER LINK (inner helper) ─────────────────────────────────────────────

function ManagerLink({ label }: { label: string }) {
  return (
    <a
      href={CONTACTS.telegram}
      target="_blank"
      rel="noopener noreferrer"
      className="rounded-xl border-2 border-primary px-6 py-3 font-medium text-primary"
    >
      {label}
    </a>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function ToolAccessGate({
  state,
  source,
  onRetry,
}: ToolAccessGateProps) {
  // loading — render nothing (not a block); the tool shows its own skeleton if needed.
  if (state.status === "loading") return null;

  // ok — unobtrusive always-visible counter (INV-C). Non-blocking.
  if (state.status === "ok") {
    return (
      <p className="text-sm text-text-muted">
        Осталось {state.remaining} из {state.maxCount} расчётов сегодня
      </p>
    );
  }

  // anon_exhausted — Telegram auth block, NO timer (INV-B). The reread on tab
  // return is done by useToolAccess (visibilitychange, INV-A) — no onAuthSuccess.
  if (state.status === "anon_exhausted") {
    return (
      <TelegramAuthBlock
        usedCount={state.usedCount}
        maxCount={state.maxCount}
        isLimitReached={true}
        source={source}
      />
    );
  }

  // cooldown — таймер ТОЛЬКО, NO auth window (INV-B). Основная кнопка инструмента
  // (Рассчитать/Анализировать/Расшифровать) дизейблится в самом инструменте по
  // state.status==='cooldown'; отдельной кнопки в gate нет (scope Василия). По
  // истечении таймера зовём onRetry?.() → инструмент/хук перечитывает состояние,
  // статус сам сменится cooldown → ok и основная кнопка разблокируется (хук всё
  // равно перечитает по visibilitychange/focus, если onRetry не передан).
  if (state.status === "cooldown") {
    return (
      <div className="rounded-2xl border border-border bg-surface p-6 text-center">
        <p className="text-sm text-text-muted">
          Можно продолжить через{" "}
          <CooldownTimer
            key={state.cooldownSeconds}
            initialSeconds={state.cooldownSeconds}
            onFinish={() => onRetry?.()}
          />
        </p>
      </div>
    );
  }

  // auth_exhausted — daily budget spent; manager path, no auth window, no timer.
  if (state.status === "auth_exhausted") {
    return (
      <div className="rounded-2xl border border-border bg-surface p-6 text-center">
        <p className="font-medium text-text">
          Дневной лимит исчерпан ({state.maxCount}/день)
        </p>
        <p className="mt-1 text-sm text-text-muted">
          Лимит обновится завтра. Нужно больше — напишите менеджеру.
        </p>
        <div className="mt-4 flex justify-center">
          <ManagerLink label="Написать менеджеру" />
        </div>
      </div>
    );
  }

  // queue_full — server at capacity; suggest retrying shortly + manager.
  if (state.status === "queue_full") {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
        <p className="font-medium text-red-800">
          {state.message ?? "Сейчас много запросов"}
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-center">
          {onRetry && (
            <button
              onClick={onRetry}
              className="rounded-xl bg-secondary px-6 py-3 font-medium text-white hover:bg-secondary-hover"
            >
              Попробовать через несколько минут
            </button>
          )}
          <ManagerLink label="Написать менеджеру — он поможет вам подобрать машину" />
        </div>
      </div>
    );
  }

  // error (default) — the quota read failed. The hook is fail-open by contract:
  // remaining / maxCount keep their last-known values and the tool stays usable, so
  // this is not a user-facing event — render nothing, exactly like `loading`. The
  // reason is emitted once by useToolAccess as a console.warn for the developer.
  return null;
}
